import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClusterListItem } from '@b4b/shared';
import { useReadAll, useHealth, useSetRead, useSetSaved, useMarkSeen } from '../../api/queries.js';
import ReelCard from '../ReelCard.js';
import FilterChips from './FilterChips.js';
import { useRelativeTime } from '../../lib/time.js';
import { useKeyboardShortcuts } from '../../lib/useKeyboardShortcuts.js';
import type { FeedParams } from '../../lib/feedUrl.js';
import type { Pager } from './FeedFooter.js';

// Reels-style feed: one story per viewport, scroll-snap between stories,
// tap opens the detail. Filters live in a slim overlay on top.
export default function ReelsFeed({
  items,
  params,
  pager,
  fetchingFirstArticles,
}: {
  items: ClusterListItem[];
  params: FeedParams;
  pager: Pager;
  fetchingFirstArticles: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const readAll = useReadAll();
  const health = useHealth();
  const relTime = useRelativeTime();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const markSeen = useMarkSeen();
  const containerRef = useRef<HTMLDivElement>(null);
  const seenMarked = useRef(new Set<string>());

  // mark stories as read once they are scrolled past (seen) — Google Reader style
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el || el.clientHeight === 0) return;
    // let the auto-hiding header know about internal scroll position
    window.dispatchEvent(new CustomEvent('b4b:scroll', { detail: { top: el.scrollTop } }));
    const index = Math.round(el.scrollTop / el.clientHeight);
    for (let i = 0; i < Math.min(index, items.length); i++) {
      const item = items[i];
      if (item && !item.read && !seenMarked.current.has(item.id)) {
        seenMarked.current.add(item.id);
        markSeen.mutate(item.id);
      }
    }
  };

  // viewport size changed (system bars, keyboard, rotation) → re-align the
  // scroll position to a whole slide; some WebViews don't re-snap on their own
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const height = el.clientHeight;
      if (height === 0) return;
      const index = Math.round(el.scrollTop / height);
      const aligned = index * height;
      if (Math.abs(el.scrollTop - aligned) > 1) el.scrollTo({ top: aligned });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // keyboard: j/k scroll a full story, rest acts on the story in view
  const currentIndex = () => {
    const el = containerRef.current;
    if (!el || el.clientHeight === 0) return 0;
    return Math.round(el.scrollTop / el.clientHeight);
  };
  const scrollToIndex = (index: number) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: index * el.clientHeight, behavior: 'smooth' });
  };
  const focusedItem = () => items[Math.min(currentIndex(), items.length - 1)];

  useKeyboardShortcuts({
    next: () => scrollToIndex(Math.min(currentIndex() + 1, items.length)),
    prev: () => scrollToIndex(Math.max(currentIndex() - 1, 0)),
    open: () => {
      const item = focusedItem();
      if (item) navigate(`/c/${item.id}`);
    },
    openOriginal: () => {
      const item = focusedItem();
      if (item) {
        window.open(item.representative.url, '_blank', 'noopener');
        if (!item.read) setRead.mutate({ id: item.id, read: true });
      }
    },
    toggleSaved: () => {
      const item = focusedItem();
      if (item) setSaved.mutate({ id: item.id, saved: !item.saved });
    },
    toggleRead: () => {
      const item = focusedItem();
      if (item) setRead.mutate({ id: item.id, read: !item.read });
    },
    markAllRead: () => readAll.mutate(new Date().toISOString()),
    search: () => navigate('/search'),
  });

  return (
    // fixed inset-0 = always exactly the visual viewport (dvh units misbehave in
    // some Android WebViews when system bars hide — a sliver of the next slide showed)
    <div className="fixed inset-0 mx-auto max-w-3xl">
      <FilterChips params={params} overlay />

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full snap-y snap-mandatory overflow-y-auto overscroll-contain [scrollbar-width:none]"
      >
        {items.map((item) => (
          <div key={item.id} className="h-full snap-start snap-always">
            <ReelCard item={item} />
          </div>
        ))}

        {/* final slide: load more / all done / still fetching first articles */}
        <div className="flex h-full snap-start snap-always flex-col items-center justify-center gap-4 px-8 text-center">
          {pager.hasMore ? (
            <button
              onClick={pager.loadMore}
              disabled={pager.loading}
              className="min-h-[44px] rounded-full border border-neutral-300 px-6 text-sm font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              {pager.loading ? t('common.loading') : t('feed.loadMore')}
            </button>
          ) : fetchingFirstArticles ? (
            <>
              <p className="animate-pulse text-3xl font-bold text-neutral-400">{t('feed.fetching')}</p>
              <p className="text-sm text-neutral-400">{t('feed.fetchingHint')}</p>
            </>
          ) : (
            <p className="text-3xl font-bold text-neutral-400">{t('feed.allDone')}</p>
          )}
          {items.length > 0 && params.filter === 'unread' && !pager.hasMore && !(params.category || params.tag || params.topic) && (
            <button
              onClick={() => readAll.mutate(new Date().toISOString())}
              className="rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white"
            >
              {t('feed.markAllRead')}
            </button>
          )}
          {health.data?.lastFetchRunAt && (
            <p className="text-xs text-neutral-400">
              {t('feed.updatedAgo', { time: relTime(health.data.lastFetchRunAt) })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
